<template>
  <v-card>
    <v-card-title> Intervention Template </v-card-title>

    <v-card-text>
      <!-- Product Type -->
      <v-select
        v-model="template.productType"
        :items="productTypes"
        label="Product Type"
        item-title="label"
        item-value="value"
        :rules="[(v) => !!v || 'Required']"
      />

      <v-divider class="my-6" />

      <!-- Items -->
      <div class="d-flex justify-space-between align-center mb-2">
        <h3>Checklist Items</h3>
        <v-btn color="primary" @click="addItem"> Add Item </v-btn>
      </div>

      <v-card
        v-for="(item, index) in template.items"
        :key="index"
        class="mb-4 pa-4"
        variant="outlined"
      >
        <div class="d-flex justify-space-between align-center mb-3">
          <strong>Item {{ index as number + 1 }}</strong>

          <div>
            <v-btn icon size="small" @click="moveUp(index)">
              <v-icon>mdi-arrow-up</v-icon>
            </v-btn>

            <v-btn icon size="small" @click="moveDown(index)">
              <v-icon>mdi-arrow-down</v-icon>
            </v-btn>

            <v-btn icon size="small" color="error" @click="removeItem(index)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <v-text-field
          v-model="item.label"
          label="Label"
          :rules="[(v) => !!v || 'Required']"
        />

        <v-select
          v-model="item.itemType"
          :items="itemTypes"
          label="Item Type"
          item-title="title"
          item-value="value"
        />

        <v-checkbox v-model="item.required" label="Required" />
      </v-card>
    </v-card-text>

    <v-card-actions class="justify-end">
      <v-btn color="primary" :loading="loading" @click="submit"> Save Template </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { InterventionItemType } from "@/types";
import { ref } from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      productType: null,
      items: [],
    }),
  },
  productTypes: {
    type: Array,
    required: true,
  },
  loading: Boolean,
});

const emit = defineEmits(["update:modelValue", "submit"]);

const template = ref(structuredClone(props.modelValue));

const itemTypes = Object.values(InterventionItemType);

function addItem() {
  template.value.items.push({
    label: "",
    itemType: "CHECKBOX",
    displayOrder: template.value.items.length + 1,
    required: false,
  });
}

function removeItem(index) {
  template.value.items.splice(index, 1);
  reorder();
}

function moveUp(index) {
  if (index === 0) return;
  const items = template.value.items;
  [items[index - 1], items[index]] = [items[index], items[index - 1]];
  reorder();
}

function moveDown(index) {
  const items = template.value.items;
  if (index === items.length - 1) return;
  [items[index + 1], items[index]] = [items[index], items[index + 1]];
  reorder();
}

function reorder() {
  template.value.items.forEach((item, i) => {
    item.displayOrder = i + 1;
  });
}

function submit() {
  reorder();
  emit("submit", template.value);
}
</script>
